import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

const PrivacidadePage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto max-w-4xl px-4 py-4 flex items-center gap-4">
          <Link to="/" className="text-muted-foreground hover:text-brand transition-colors flex items-center gap-2 text-sm">
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Link>
          <div className="flex items-center gap-2 ml-auto">
            <img src="/logo-altus.png" alt="Altus Food" className="h-8 w-auto" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto max-w-4xl px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-2.5 rounded-xl gradient-brand">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground">
            Política de Privacidade
          </h1>
        </div>

        <p className="text-sm text-muted-foreground mb-10">Última atualização: março de 2026</p>

        <div className="space-y-10 text-foreground/90 leading-relaxed">
          <Section title="1. Introdução">
            <p>
              O aplicativo AltusFood Garçom ("Aplicativo") é desenvolvido e operado pela Actual TI. 
              Esta Política de Privacidade descreve como coletamos, usamos e protegemos as informações 
              dos usuários do Aplicativo.
            </p>
          </Section>

          <Section title="2. Informações que Coletamos">
            <p className="mb-4">O Aplicativo coleta apenas as informações estritamente necessárias para seu funcionamento:</p>
            <ul className="list-disc list-inside space-y-1.5 text-muted-foreground mb-6">
              <li>Credenciais de login (e-mail e senha) para autenticação</li>
              <li>Dados de pedidos lançados durante o uso do app</li>
              <li>Informações do dispositivo (modelo, versão do Android) para fins de compatibilidade e suporte técnico</li>
              <li>Dados de conexão de rede para verificar conectividade</li>
            </ul>
            <p className="mb-4 font-semibold">O Aplicativo NÃO coleta:</p>
            <ul className="list-disc list-inside space-y-1.5 text-muted-foreground">
              <li>Dados de localização</li>
              <li>Contatos do dispositivo</li>
              <li>Fotos ou arquivos pessoais</li>
              <li>Dados bancários ou financeiros pessoais</li>
            </ul>
          </Section>

          <Section title="3. Como Usamos as Informações">
            <p className="mb-4">As informações coletadas são usadas exclusivamente para:</p>
            <ul className="list-disc list-inside space-y-1.5 text-muted-foreground">
              <li>Autenticar o usuário no sistema AltusFood</li>
              <li>Registrar e transmitir pedidos ao estabelecimento</li>
              <li>Garantir o funcionamento correto do Aplicativo</li>
              <li>Prestar suporte técnico quando solicitado</li>
            </ul>
          </Section>

          <Section title="4. Compartilhamento de Dados">
            <p>
              Os dados são transmitidos apenas para os servidores do Sistema AltusFood do estabelecimento contratante. 
              Não compartilhamos dados pessoais com terceiros, exceto quando exigido por lei.
            </p>
          </Section>

          <Section title="5. Armazenamento e Segurança">
            <p>
              Os dados são armazenados de forma segura utilizando criptografia em trânsito (HTTPS/TLS). 
              O acesso é restrito a usuários autenticados com credenciais válidas.
            </p>
          </Section>

          <Section title="6. Retenção de Dados">
            <p>
              Os dados de pedidos são mantidos conforme a política do estabelecimento contratante do Sistema AltusFood. 
              Credenciais de acesso podem ser removidas mediante solicitação ao administrador do sistema.
            </p>
          </Section>

          <Section title="7. Direitos do Usuário">
            <p className="mb-4">O usuário tem direito a:</p>
            <ul className="list-disc list-inside space-y-1.5 text-muted-foreground mb-4">
              <li>Acessar seus dados pessoais armazenados</li>
              <li>Solicitar correção de dados incorretos</li>
              <li>Solicitar exclusão de seus dados</li>
              <li>Revogar o acesso ao Aplicativo</li>
            </ul>
            <p>
              Para exercer esses direitos, entre em contato:{" "}
              <a href="mailto:contato@actualti.com.br" className="text-brand hover:underline">
                contato@actualti.com.br
              </a>
            </p>
          </Section>

          <Section title="8. Contato">
            <div className="space-y-1 text-muted-foreground">
              <p className="font-semibold text-foreground">Actual TI</p>
              <p>E-mail: <a href="mailto:contato@actualti.com.br" className="text-brand hover:underline">contato@actualti.com.br</a></p>
              <p>Site: <a href="https://www.sejaaltus.com.br" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">www.sejaaltus.com.br</a></p>
              <p>Araraquara — SP — Brasil</p>
            </div>
          </Section>

          <Section title="9. Alterações nesta Política">
            <p>
              Esta política pode ser atualizada periodicamente. Recomendamos verificar esta página regularmente. 
              Alterações significativas serão comunicadas pelo próprio Aplicativo.
            </p>
          </Section>

          <div className="pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground italic">
              Ao utilizar o AltusFood Garçom, você concorda com os termos desta Política de Privacidade.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-6 mt-12">
        <div className="container mx-auto max-w-4xl px-4 text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} Altus Soluções. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section>
    <h2 className="text-lg font-display font-semibold text-foreground mb-3">{title}</h2>
    {children}
  </section>
);

export default PrivacidadePage;
